import * as React from 'react'
import { View, StyleSheet, ViewStyle } from 'react-native'
import { SelectableButton } from './SelectableButton'
import { useBreakpoints, ResponsiveValue } from './hooks/useBreakpoints'

interface Option<T extends string | number = string> {
	label: string
	value: T
	description?: string
}

interface SegmentedButtonProps<T extends string | number = string> {
	options: Option<T>[]
	selectedValue: T
	onSelect: (option: Option<T>) => void
	size?: 'sm' | 'md' | 'lg' | ResponsiveValue<'sm' | 'md' | 'lg'>
	disabled?: boolean
	style?: ViewStyle
	buttonStyle?: ViewStyle
}

export function SegmentedButton<T extends string | number = string>({
	options,
	selectedValue,
	onSelect,
	size = 'md',
	disabled = false,
	style,
	buttonStyle,
}: SegmentedButtonProps<T>) {
	const { value } = useBreakpoints()
	const sizeValue = typeof size === 'object' ? value(size) : size

	return (
		<View style={[styles.container, style]}>
			{options.map((option, index) => {
				const isSelected = option.value === selectedValue
				const isFirst = index === 0
				const isLast = index === options.length - 1

				const customButtonStyle: ViewStyle = {
					...styles.button,
					...(isFirst && styles.firstButton),
					...(isLast && styles.lastButton),
					...(!isFirst && !isLast && styles.middleButton),
					...buttonStyle,
				}

				return (
					<SelectableButton
						key={String(option.value)} // Convert to string for key
						label={option.label}
						selected={isSelected}
						onPress={() => !disabled && onSelect(option)}
						size={sizeValue}
						disabled={disabled}
						style={customButtonStyle}
						description={option.description}
					/>
				)
			})}
		</View>
	)
}

const styles = StyleSheet.create({
	container: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	button: {
		flex: 1,
		borderRadius: 0,
		marginHorizontal: 0,
		borderWidth: 1,
		minHeight: 58,
		maxHeight: 72,
	},
	firstButton: {
		borderTopLeftRadius: 16,
		borderBottomLeftRadius: 16,
		borderRightWidth: 1,
	},
	lastButton: {
		borderTopRightRadius: 16,
		borderBottomRightRadius: 16,
		borderLeftWidth: 0,
	},
	middleButton: {
		borderLeftWidth: 0,
	},
})
